import { getAuthRequestHeaders } from "@qw/auth";
import { File_Audience, type File_Source, type PolicyFileType } from "@qw/graphql";
import type { MaterialSymbol } from "material-symbols";
import { z } from "zod";

export interface UploadFileOptions {
  insuredId?: string;
  audience: File_Audience;
  source?: File_Source;
}

// Files at/under the old Cloud Run request-body cap can still go through the legacy
// multipart endpoints, so we fall back to them when direct upload fails for them.
const LEGACY_FALLBACK_MAX_BYTES = 30 * 1024 * 1024;
const UPLOAD_URL_TIMEOUT_MS = 10_000;

const createTimeoutSignal = (timeoutMs: number): { cancel: () => void; signal: AbortSignal } => {
  if (typeof AbortSignal.timeout === "function") {
    return { cancel: () => undefined, signal: AbortSignal.timeout(timeoutMs) };
  }

  const controller = new AbortController();
  const timeoutId = globalThis.setTimeout(() => controller.abort(), timeoutMs);
  return { cancel: () => globalThis.clearTimeout(timeoutId), signal: controller.signal };
};

export const FileUploadDataSchema = z.object({
  id: z.string(),
  filename: z.string(),
  mimeType: z.string(),
  size: z.number().optional(),
});

export type FileUploadData = z.infer<typeof FileUploadDataSchema>;

export type PartialFileUpload = Pick<FileUploadData, "id" | "filename" | "mimeType">;

export type FilePreviewKind = "email" | "image" | "pdf";

const FILE_PREVIEW_KIND_BY_MIME_TYPE = new Map<string, FilePreviewKind>([
  ["application/pdf", "pdf"],
  ["image/jpeg", "image"],
  ["image/png", "image"],
  ["image/gif", "image"],
  ["image/webp", "image"],
  ["message/rfc822", "email"],
]);

export const PREVIEWABLE_MIME_TYPES = new Set(FILE_PREVIEW_KIND_BY_MIME_TYPE.keys());

export const getNormalizedMimeType = (mimeType: string | null | undefined) =>
  mimeType?.split(";")[0]?.trim().toLowerCase();

export const getFilePreviewKind = (mimeType: string | null | undefined) => {
  const normalizedMimeType = getNormalizedMimeType(mimeType);

  return normalizedMimeType ? FILE_PREVIEW_KIND_BY_MIME_TYPE.get(normalizedMimeType) : undefined;
};

export const isPreviewable = (mimeType: string | null | undefined): mimeType is string =>
  Boolean(getFilePreviewKind(mimeType));

export const downloadBlob = (blob: Blob, name: string) => {
  const url = globalThis.URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.style.display = "none";
  a.href = url;
  a.download = name;
  document.body.append(a);
  a.click();
  globalThis.URL.revokeObjectURL(url);
  a.remove();
};

interface FileDownloadUrl {
  signedUrl: string | null;
  fallbackUrl?: string;
  filename?: string;
  /** Absent from an API that predates inline previews. */
  disposition?: "attachment" | "inline";
}

/**
 * Gets a signed GCS URL for a file. `inline` lets the browser render it (e.g. its PDF viewer) under the real filename.
 * `signedUrl` is null for legacy Google Drive files.
 */
export const getFileDownloadUrl = async (
  fileId: string,
  {
    disposition = "attachment",
    headers = getAuthRequestHeaders(),
  }: { disposition?: "attachment" | "inline"; headers?: HeadersInit } = {}
): Promise<FileDownloadUrl> => {
  const url = new URL(`/api/files/${fileId}/download-url`, globalThis.location.origin);

  if (disposition === "inline") {
    url.searchParams.set("disposition", "inline");
  }

  const response = await fetch(url.toString(), { headers });

  if (!response.ok) {
    throw new Error(`Failed to get download URL: ${response.status}`);
  }

  return (await response.json()) as FileDownloadUrl;
};

export const downloadFile = async (file: FileUploadData) => {
  const data = await getFileDownloadUrl(file.id);

  if (data.signedUrl) {
    // Use signed URL - browser handles download natively
    // Using anchor element for cleaner download behavior
    const anchor = document.createElement("a");
    anchor.href = data.signedUrl;
    anchor.download = file.filename;
    document.body.append(anchor);
    anchor.click();
    anchor.remove();
  } else {
    // Fallback for legacy Google Drive files - load into memory
    const blob = await fetch(data.fallbackUrl ?? `/api/files/${file.id}`, {
      headers: getAuthRequestHeaders(),
    })
      .then((r) => r.blob())
      .then((b) => b.slice(0, b?.size ?? 0, file.mimeType));

    downloadBlob(blob, file.filename);
  }
};

/**
 * Uploads a file directly to GCS via a signed URL when the server has the
 * direct-upload feature enabled for the current user. Returns { enabled: false }
 * only when the server gates the user out via a successful mint response, so
 * callers fall back to the legacy multipart path. Mint failures throw so
 * callers can distinguish "flag off" from "direct upload failed" before
 * deciding whether the legacy size cap applies. The PUT goes cross-origin
 * straight to GCS, so it carries only the Content-Type header (any unsigned
 * header would break the signature).
 */
export async function uploadToStorage(
  file: File
): Promise<{ enabled: false } | { enabled: true; storageId: string; filename: string; mimeType: string }> {
  const contentType = file.type || "application/octet-stream";

  const mintTimeout = createTimeoutSignal(UPLOAD_URL_TIMEOUT_MS);
  let mint: Response;
  try {
    mint = await fetch("/api/files/upload-url", {
      method: "POST",
      headers: { ...getAuthRequestHeaders(), "Content-Type": "application/json" },
      body: JSON.stringify({ filename: file.name, contentType }),
      signal: mintTimeout.signal,
    });
  } finally {
    mintTimeout.cancel();
  }

  if (!mint.ok) {
    throw new Error(`Failed to get upload URL: ${mint.status}`);
  }

  const result = (await mint.json()) as { enabled: false } | { enabled: true; storageId: string; signedUrl: string };

  if (!result.enabled) {
    return { enabled: false };
  }

  const put = await fetch(result.signedUrl, {
    method: "PUT",
    headers: { "Content-Type": contentType },
    body: file,
  });

  if (!put.ok) {
    throw new Error(`Failed to upload file to storage: ${put.status}`);
  }

  return { enabled: true, storageId: result.storageId, filename: file.name, mimeType: contentType };
}

type StorageUploadResult = Awaited<ReturnType<typeof uploadToStorage>>;

export async function uploadFiles(
  files: File[],
  options?: UploadFileOptions
): Promise<{ fileId: string[]; fileData: FileUploadData[] }> {
  const { insuredId, audience, source } = options || {};

  // Direct-to-GCS path (feature-flagged on per user via the mint endpoint): PUT each
  // file straight to the ingest bucket, then register the batch by storageId instead
  // of streaming the bytes through the API.
  let uploads: StorageUploadResult[] | null;
  // uploadToStorage returns { enabled: false } only after the server explicitly gates
  // the user out. Any throw means direct upload failed, so the legacy size cap applies.
  let directUploadFailed = false;
  try {
    uploads = await Promise.all(files.map((file) => uploadToStorage(file)));
  } catch {
    uploads = null;
    directUploadFailed = true;
  }

  // If every file minted (flag on for this user), register the batch directly.
  // A registration failure must surface because objects may already be registered
  // server-side and retrying through legacy multipart can duplicate files.
  if (uploads && uploads.length > 0 && uploads.every((upload) => upload.enabled)) {
    const res = await fetch(`/api/files/from-storage`, {
      method: "POST",
      headers: { ...getAuthRequestHeaders(), "Content-Type": "application/json" },
      body: JSON.stringify({
        files: uploads.map((upload) => ({
          storageId: upload.storageId,
          filename: upload.filename,
          mimeType: upload.mimeType,
        })),
        insuredId,
        audience,
        source,
      }),
    });

    if (!res.ok) {
      throw new Error(`Failed to register uploaded files: ${res.status}`);
    }

    const { fileId, fileData } = (await res.json()) as { fileId: string[]; fileData: FileUploadData[] };
    return { fileId, fileData };
  }

  // Legacy multipart path. When the flag is off we keep the legacy route's old behavior
  // for all sizes so the dark launch stays invisible. Only after an actual direct-upload
  // attempt failed do we guard the old Cloud Run request-body cap client-side, so an
  // oversized batch fails deterministically instead of posting to /api/files and getting
  // a 413.
  if (directUploadFailed) {
    const totalSize = files.reduce((sum, file) => sum + file.size, 0);
    if (totalSize > LEGACY_FALLBACK_MAX_BYTES) {
      throw new Error("This upload is too large for the legacy upload path. Upload a smaller batch.");
    }
  }

  const fd = new FormData();

  files.map((file) => fd.append("files", file));

  if (insuredId) {
    fd.set("insuredId", insuredId);
  }
  if (audience) {
    fd.set("audience", audience);
  }
  if (source) {
    fd.set("source", source);
  }

  const res = await fetch(`/api/files`, {
    method: "POST",
    headers: getAuthRequestHeaders(),
    body: fd,
  });

  const { fileId, fileData } = (await res.json()) as { fileId: string[]; fileData: FileUploadData[] };

  return { fileId, fileData };
}

export async function uploadRedactedQuote(file: File, submissionId: string) {
  // Direct-to-GCS path (feature-flagged on per user via the mint endpoint): register
  // the already-uploaded object instead of streaming bytes through the API. On failure,
  // fall back to the legacy path for files small enough to fit the old request cap so a
  // transient GCS/CORS issue doesn't regress ordinary uploads for flagged users; larger
  // files (which legacy can't carry) resolve to { success: false } rather than rejecting.
  let upload: Awaited<ReturnType<typeof uploadToStorage>>;
  let directUploadFailed = false;
  try {
    upload = await uploadToStorage(file);
  } catch {
    directUploadFailed = true;
    upload = { enabled: false };
  }

  if (upload.enabled) {
    // Bytes are already in GCS — register them. A register failure must NOT fall back
    // to legacy: the object may already be registered server-side, so a retry would
    // duplicate it. Surface the failure instead.
    try {
      const res = await fetch(`/api/quotes/from-storage`, {
        method: "POST",
        headers: { ...getAuthRequestHeaders(), "Content-Type": "application/json" },
        body: JSON.stringify({
          submissionId,
          storageId: upload.storageId,
          filename: upload.filename,
          mimeType: upload.mimeType,
        }),
      });

      if (res.ok) {
        const json = (await res.json()) as { fileId: string; success: boolean };
        return { fileId: json.fileId, success: json.success };
      }
    } catch {
      // fall through to the failure return below
    }
    return { fileId: "", success: false };
  }

  // If the flag is off, keep the legacy route's old behavior for all sizes so
  // the dark launch stays invisible. Only cap fallback after an actual direct
  // upload failure.
  if (directUploadFailed && file.size > LEGACY_FALLBACK_MAX_BYTES) {
    return { fileId: "", success: false };
  }

  // Legacy multipart path (flag off, or direct upload failed for a sub-cap file).
  const fd = new FormData();
  fd.set("file", file);
  fd.set("submissionId", submissionId);

  const res = await fetch(`/api/quotes`, {
    method: "POST",
    headers: getAuthRequestHeaders(),
    body: fd,
  });

  const json = (await res.json()) as { fileId: string; success: boolean };

  return { fileId: json.fileId, success: json.success };
}

export interface CandidateProduct {
  id: string;
  carrierName: string;
  productName: string;
}

export interface UploadQuoteFromOpportunityResult {
  success: boolean;
  needsManualSelection?: boolean;
  fileId?: string;
  quoteId?: string;
  submissionId?: string;
  wasSubmissionCreated?: boolean;
  extractedCarrierName?: string | null;
  extractedProductName?: string | null;
  extractedCoverages?: string[];
  matchedCarrierName?: string;
  matchedProductName?: string;
  candidateProducts?: CandidateProduct[];
}

/**
 * Upload a quote file directly from an opportunity page.
 * This will extract carrier/product info and create a submission if needed.
 */
export async function uploadQuoteFromOpportunity(
  file: File,
  opportunityId: string,
  appetiteProductId?: string
): Promise<UploadQuoteFromOpportunityResult> {
  // Direct-to-GCS path (feature-flagged on per user via the mint endpoint): register
  // the already-uploaded object instead of streaming bytes through the API. The rich
  // result shape (match/extraction/manual-selection) is identical on both paths. On
  // failure, fall back to the legacy path for files small enough to fit the old request
  // cap; larger files (which legacy can't carry) resolve to { success: false }.
  let upload: Awaited<ReturnType<typeof uploadToStorage>>;
  let directUploadFailed = false;
  try {
    upload = await uploadToStorage(file);
  } catch {
    directUploadFailed = true;
    upload = { enabled: false };
  }

  if (upload.enabled) {
    // Register the already-uploaded object. A register failure must NOT fall back to
    // legacy (it may have succeeded server-side -> duplicate); surface failure instead.
    try {
      const res = await fetch(`/api/quotes/opportunity/${opportunityId}/from-storage`, {
        method: "POST",
        headers: { ...getAuthRequestHeaders(), "Content-Type": "application/json" },
        body: JSON.stringify({
          storageId: upload.storageId,
          filename: upload.filename,
          mimeType: upload.mimeType,
          ...(appetiteProductId ? { appetiteProductId } : {}),
        }),
      });

      if (res.ok) {
        return (await res.json()) as UploadQuoteFromOpportunityResult;
      }
    } catch {
      // fall through to the failure return below
    }
    return { success: false };
  }

  // If the flag is off, keep the legacy route's old behavior for all sizes so
  // the dark launch stays invisible. Only cap fallback after an actual direct
  // upload failure.
  if (directUploadFailed && file.size > LEGACY_FALLBACK_MAX_BYTES) {
    return { success: false };
  }

  // Legacy multipart path (flag off, or direct upload failed for a sub-cap file).
  const fd = new FormData();
  fd.set("file", file);
  if (appetiteProductId) {
    fd.set("appetiteProductId", appetiteProductId);
  }

  const res = await fetch(`/api/quotes/opportunity/${opportunityId}`, {
    method: "POST",
    headers: getAuthRequestHeaders(),
    body: fd,
  });

  if (!res.ok) {
    return { success: false };
  }

  return res.json() as Promise<UploadQuoteFromOpportunityResult>;
}

/**
 * Complete quote creation after manual product selection.
 */
export async function completeQuoteFromOpportunity(
  opportunityId: string,
  fileId: string,
  appetiteProductId: string,
  extractedCoverages?: string[]
): Promise<UploadQuoteFromOpportunityResult> {
  const res = await fetch(`/api/quotes/opportunity/${opportunityId}/complete`, {
    method: "POST",
    headers: {
      ...getAuthRequestHeaders(),
      "Content-Type": "application/json",
    },
    body: JSON.stringify({ fileId, appetiteProductId, extractedCoverages }),
  });

  if (!res.ok) {
    return { success: false };
  }

  return res.json() as Promise<UploadQuoteFromOpportunityResult>;
}

export interface QuoteUploadTaskFileResult {
  id: string;
  filename: string;
  mimeType: string;
  label: string | null;
  url: string | null;
}

export type QuoteUploadTaskFileOutcome = "QUOTE_CREATED" | "DECLINED";

/**
 * Attach a quote-upload artifact to an Alpine task. Quote-created uploads are
 * PDFs; declined uploads are decision images.
 */
export async function uploadQuoteUploadTaskFile(
  file: File,
  options: { taskId: string; outcome?: QuoteUploadTaskFileOutcome }
): Promise<{ success: boolean; file?: QuoteUploadTaskFileResult }> {
  // Direct-to-GCS path (feature-flagged on per user via the mint endpoint): register
  // the already-uploaded object instead of streaming bytes through the API. On failure,
  // fall back to the legacy path for files small enough to fit the old request cap so a
  // transient GCS/CORS issue doesn't regress ordinary uploads for flagged users; larger
  // files (which legacy can't carry) resolve to { success: false } rather than rejecting.
  let upload: Awaited<ReturnType<typeof uploadToStorage>>;
  let directUploadFailed = false;
  try {
    upload = await uploadToStorage(file);
  } catch {
    directUploadFailed = true;
    upload = { enabled: false };
  }

  if (upload.enabled) {
    // Register the already-uploaded object. A register failure must NOT fall back to
    // legacy — quote-upload-task uploads aren't idempotent, so a retry after a register
    // that may have succeeded server-side would double-attach. Surface failure instead.
    try {
      const res = await fetch(`/api/alpine/quote-upload-tasks/${options.taskId}/files/from-storage`, {
        method: "POST",
        headers: { ...getAuthRequestHeaders(), "Content-Type": "application/json" },
        body: JSON.stringify({
          storageId: upload.storageId,
          filename: upload.filename,
          mimeType: upload.mimeType,
          outcome: options.outcome,
        }),
      });

      if (res.ok) {
        return res.json() as Promise<{ success: boolean; file?: QuoteUploadTaskFileResult }>;
      }
    } catch {
      // fall through to the failure return below
    }
    return { success: false };
  }

  // If the flag is off, keep the legacy route's old behavior for all sizes so
  // the dark launch stays invisible. Only cap fallback after an actual direct
  // upload failure.
  if (directUploadFailed && file.size > LEGACY_FALLBACK_MAX_BYTES) {
    return { success: false };
  }

  // Legacy multipart path (flag off, or direct upload failed for a sub-cap file).
  const fd = new FormData();
  fd.set("file", file);
  if (options.outcome) {
    fd.set("outcome", options.outcome);
  }

  const res = await fetch(`/api/alpine/quote-upload-tasks/${options.taskId}/files`, {
    method: "POST",
    headers: getAuthRequestHeaders(),
    body: fd,
  });

  if (!res.ok) {
    return { success: false };
  }

  return res.json() as Promise<{ success: boolean; file?: QuoteUploadTaskFileResult }>;
}

export async function uploadProcessedQuote(file: File, quoteId: string) {
  // Direct-to-GCS path (feature-flagged on per user via the mint endpoint): register
  // the already-uploaded object instead of streaming bytes through the API. On failure,
  // fall back to the legacy path for files small enough to fit the old request cap so a
  // transient GCS/CORS issue doesn't regress ordinary uploads for flagged users; larger
  // files (which legacy can't carry) resolve to { success: false } rather than rejecting.
  let upload: Awaited<ReturnType<typeof uploadToStorage>>;
  let directUploadFailed = false;
  try {
    upload = await uploadToStorage(file);
  } catch {
    directUploadFailed = true;
    upload = { enabled: false };
  }

  if (upload.enabled) {
    // Register the already-uploaded object. A register failure must NOT fall back to
    // legacy (it may have succeeded server-side -> duplicate); surface failure instead.
    try {
      const res = await fetch(`/api/quotes/${quoteId}/files/from-storage`, {
        method: "POST",
        headers: { ...getAuthRequestHeaders(), "Content-Type": "application/json" },
        body: JSON.stringify({
          storageId: upload.storageId,
          filename: upload.filename,
          mimeType: upload.mimeType,
          fileType: "ProcessedQuote",
        }),
      });

      if (res.ok) {
        const json = (await res.json()) as { fileId: string; success: boolean };
        return { fileId: json.fileId, success: json.success };
      }
    } catch {
      // fall through to the failure return below
    }
    return { fileId: "", success: false };
  }

  // If the flag is off, keep the legacy route's old behavior for all sizes so
  // the dark launch stays invisible. Only cap fallback after an actual direct
  // upload failure.
  if (directUploadFailed && file.size > LEGACY_FALLBACK_MAX_BYTES) {
    return { fileId: "", success: false };
  }

  // Legacy multipart path (flag off, or direct upload failed for a sub-cap file).
  const fd = new FormData();
  fd.set("file", file);
  fd.set("fileType", "ProcessedQuote");

  const res = await fetch(`/api/quotes/${quoteId}/files`, {
    method: "POST",
    headers: getAuthRequestHeaders(),
    body: fd,
  });

  const json = (await res.json()) as { fileId: string; success: boolean };

  return { fileId: json.fileId, success: json.success };
}

export async function uploadPolicyDocument(
  file: File,
  policyId: string,
  fileType: PolicyFileType,
  documentId?: string
) {
  // Direct-to-GCS path (feature-flagged on per user via the mint endpoint): register
  // the already-uploaded object instead of streaming bytes through the API. On failure,
  // fall back to the legacy path for files small enough to fit the old request cap so a
  // transient GCS/CORS issue doesn't regress ordinary uploads for flagged users; larger
  // files (which legacy can't carry) resolve to { success: false } rather than rejecting.
  let upload: Awaited<ReturnType<typeof uploadToStorage>>;
  let directUploadFailed = false;
  try {
    upload = await uploadToStorage(file);
  } catch {
    directUploadFailed = true;
    upload = { enabled: false };
  }

  if (upload.enabled) {
    // Register the already-uploaded object. A register failure must NOT fall back to
    // legacy (it may have succeeded server-side -> duplicate); surface failure instead.
    try {
      const res = await fetch(`/api/policies/${policyId}/files/from-storage`, {
        method: "POST",
        headers: { ...getAuthRequestHeaders(), "Content-Type": "application/json" },
        body: JSON.stringify({
          storageId: upload.storageId,
          filename: upload.filename,
          mimeType: upload.mimeType,
          audience: File_Audience.External,
          fileType,
          documentId,
        }),
      });

      if (res.ok) {
        const json = (await res.json()) as { fileId: string; success: boolean };
        return { fileId: json.fileId, success: json.success };
      }
    } catch {
      // fall through to the failure return below
    }
    return { fileId: "", success: false };
  }

  // If the flag is off, keep the legacy route's old behavior for all sizes so
  // the dark launch stays invisible. Only cap fallback after an actual direct
  // upload failure.
  if (directUploadFailed && file.size > LEGACY_FALLBACK_MAX_BYTES) {
    return { fileId: "", success: false };
  }

  // Legacy multipart path (flag off, or direct upload failed for a sub-cap file).
  const fd = new FormData();
  fd.set("file", file);
  fd.set("audience", File_Audience.External);
  fd.set("fileType", fileType);
  if (documentId) {
    fd.set("documentId", documentId);
  }

  const res = await fetch(`/api/policies/${policyId}/files`, {
    method: "POST",
    headers: getAuthRequestHeaders(),
    body: fd,
  });

  const json = (await res.json()) as { fileId: string; success: boolean };

  return { fileId: json.fileId, success: json.success };
}

export interface UploadCarrierContractResult {
  success: boolean;
  contract?: {
    id: string;
    fileUploadId: string;
    extractionStatus: string;
    filename: string;
  };
}

/**
 * Upload a carrier contract document. Mirrors uploadPolicyDocument: try the
 * direct-to-GCS path first (feature-flagged on per user via the mint endpoint) by
 * registering the already-uploaded object, and fall back to the legacy multipart
 * endpoint for files small enough to fit the old request cap so a transient
 * GCS/CORS issue doesn't regress ordinary uploads for flagged users; larger files
 * (which legacy can't carry) resolve to { success: false } rather than rejecting.
 * The legacy path is untouched when the flag is off.
 */
export async function uploadCarrierContract(
  file: File,
  carrierId: string,
  appetiteProductIds: string[]
): Promise<UploadCarrierContractResult> {
  let upload: Awaited<ReturnType<typeof uploadToStorage>>;
  let directUploadFailed = false;
  try {
    upload = await uploadToStorage(file);
  } catch {
    directUploadFailed = true;
    upload = { enabled: false };
  }

  if (upload.enabled) {
    // Register the already-uploaded object. A register failure must NOT fall back to
    // legacy: the server may have already persisted the upload / created the contract,
    // so a retry would create a duplicate contract. Surface the failure instead.
    try {
      const res = await fetch(`/api/carriers/${carrierId}/contracts/upload/from-storage`, {
        method: "POST",
        headers: { ...getAuthRequestHeaders(), "Content-Type": "application/json" },
        body: JSON.stringify({
          storageId: upload.storageId,
          filename: upload.filename,
          mimeType: upload.mimeType,
          ...(appetiteProductIds.length > 0 && { appetiteProductIds }),
        }),
      });

      if (res.ok) {
        return res.json() as Promise<UploadCarrierContractResult>;
      }
    } catch {
      // fall through to the failure return below
    }
    return { success: false };
  }

  // If the flag is off, keep the legacy route's old behavior for all sizes so
  // the dark launch stays invisible. Only cap fallback after an actual direct
  // upload attempt failed.
  if (directUploadFailed && file.size > LEGACY_FALLBACK_MAX_BYTES) {
    return { success: false };
  }

  // Legacy multipart path (flag off, or direct upload failed for a sub-cap file).
  const fd = new FormData();
  fd.append("file", file);
  if (appetiteProductIds.length > 0) {
    fd.append("appetiteProductIds", JSON.stringify(appetiteProductIds));
  }

  const res = await fetch(`/api/carriers/${carrierId}/contracts/upload`, {
    method: "POST",
    headers: getAuthRequestHeaders(),
    body: fd,
  });

  return res.json() as Promise<UploadCarrierContractResult>;
}

export const formatFileSize = (bytes: number) => {
  if (bytes === 0) {
    return "0 Bytes";
  }
  const k = 1024;
  const sizes = ["Bytes", "KB", "MB", "GB"];
  const i = Math.floor(Math.log(bytes) / Math.log(k));
  return `${Number.parseFloat((bytes / Math.pow(k, i)).toFixed(2))} ${sizes[i]}`;
};

export const parseFilename = (filename: string): { name: string; extension: string | undefined } => {
  const lastDotIndex = filename.lastIndexOf(".");
  if (lastDotIndex === -1 || lastDotIndex === filename.length - 1) {
    return { name: filename, extension: undefined };
  }
  return {
    name: filename.slice(0, Math.max(0, lastDotIndex)),
    extension: filename.slice(Math.max(0, lastDotIndex + 1)),
  };
};

export const fileExtensionFromMimeType = (mimeType: string): string => {
  switch (mimeType) {
    case "application/pdf":
      return "pdf";
    case "application/msword":
      return "doc";
    case "application/vnd.openxmlformats-officedocument.wordprocessingml.document":
      return "docx";
    case "application/vnd.ms-excel":
      return "xls";
    case "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet":
      return "xlsx";
    case "application/zip":
      return "zip";
    case "image/png":
      return "png";
    case "image/jpeg":
      return "jpg";
    case "image/jpg":
      return "jpg";
    case "image/gif":
      return "gif";
    case "image/bmp":
      return "bmp";
    case "image/tiff":
      return "tiff";
    case "image/webp":
      return "webp";
    case "image/svg+xml":
      return "svg";
    case "image/ico":
      return "ico";
    case "image/heic":
      return "heic";
    case "image/heif":
      return "heif";
    case "image/heif-sequence":
      return "heif-sequence";
    case "text/plain":
      return "txt";
    case "text/csv":
      return "csv";
    case "text/tab-separated-values":
      return "tsv";
    case "text/html":
      return "html";
    case "text/xml":
      return "xml";
    case "text/javascript":
      return "js";
    case "text/css":
      return "css";
    case "text/php":
      return "php";
    case "text/python":
      return "py";
    case "text/ruby":
      return "rb";
    case "text/java":
      return "java";
    case "text/c":
      return "c";
    case "text/cpp":
      return "cpp";
    case "text/sql":
      return "sql";
    case "text/json":
      return "json";
    case "text/yaml":
      return "yaml";
    case "text/toml":
      return "toml";
    case "text/markdown":
      return "md";
    case "message/rfc822":
      return "eml";
    case "video/mp4":
      return "mp4";
    case "video/webm":
      return "webm";
    case "video/ogg":
      return "ogg";
    case "video/quicktime":
      return "mov";
    case "video/mpeg":
      return "mpeg";
    case "audio/mpeg":
      return "mp3";
    case "audio/wav":
      return "wav";
    default:
      return mimeType.split("/")[1] ?? "unknown";
  }
};

export const getFileIcon = (mimeType: string): MaterialSymbol => {
  if (mimeType.startsWith("image/")) {
    return "image";
  }
  if (mimeType.startsWith("application/zip")) {
    return "folder_zip";
  }
  if (mimeType.startsWith("application/")) {
    return "article";
  }
  if (mimeType.startsWith("text/")) {
    return "article";
  }
  if (mimeType.startsWith("video/")) {
    return "video_file";
  }
  if (mimeType.startsWith("audio/")) {
    return "audio_file";
  }
  return "draft";
};
